import dayjs from 'dayjs';

import { DoctorMedicineType, UserProfileRelationType } from '@/models/common';
import { BookingScheduleType, DayScheduleStatus, DiagnosisFormType, MedicineType } from '@/types/bff/gql-schema';
import { EDiagnosisContactType } from '@/types/diagnosis';
import { validateSsnCheck } from '@/utils/validate';

import { EDoctorSortType } from '../doctor/telemedicine';
import { DiagnosisBookingChange } from './booking-change';

export { EDiagnosisContactType };

export enum DiagnosisCompleteStep {
  Find = 'find',
  Complete = 'complete',
}

export interface DiagnosisBookingChangeQueries {
  doctorId: string;
  diagnosisDate: string;
  diagnosisTypeId: string;
  diagnosisType: string;
}

export interface DiagnosisCompleteQueries {
  doctorId: string;
  diagnosisDate: string;
  treatmentMedicineId: string;
  medicalCategoryName?: string;
  diagnosisCategoryType: DiagnosisCategoryType;
  diagnosisCategoryTypeId: string;
  step?: DiagnosisCompleteStep;
}

export interface Medicine {
  id: string | null;
  medicineName: string;
}

export interface CategoryInfo {
  id: string;
  name: string;
  type: DiagnosisCategoryType;
  hasMedicine: () => boolean;
  medicines: Medicine[];
}

export enum EDiagnosisPriceType {
  DiagnosisOnly = 'DIAGNOSIS_ONLY',
  WithDispensation = 'WITH_DISPENSATION',
}

export enum DiagnosisCategoryType {
  Treatment = 'symptom',
  Department = 'subject',
  Vaccination = 'vaccination',
}

export enum TreatmentRequestInflowType {
  GENERAL = 'GENERAL',
  QUICK = 'QUICK',
  WEB_GENERAL = 'WEB_GENERAL',
  WEB_QUICK = 'WEB_QUICK',
}

export enum DiagnosisStep {
  Diagnosis = 'diagnosis',
  Payment = 'payment',
  Doctor = 'doctor',
  CertificationGuide = 'user-verification-guide',
}

export enum DiagnosisRequestCallType {
  Video = 'VIDEO',
  Audio = 'AUDIO',
}

export interface DiagnosisTreatmentRequestPageQueries {
  brandName?: string;
  type?: DiagnosisCategoryType;
  categoryId?: string;
  doctorId: string;
  userTargetId: string;
  step?: DiagnosisStep;
  targetDate?: string;
  startTimeSlotValue?: string;
  endTimeSlotValue?: string;
  inflowType: TreatmentRequestInflowType | null;
  contactType: EDiagnosisContactType;
  pharmacyId?: string;
  treatmentMedicineUnitId?: string;
  dispensationUnit?: string;
  treatmentMedicineStrengthId?: string;
  treatmentMedicineId?: string;
  priceType?: EDiagnosisPriceType;
  sortKey?: EDoctorSortType;
  specialistOnly?: boolean;
  vaccinationTypeId?: string;
  vaccinationUnitId?: string;
  vaccinationFreePolicyId?: string;
}

export interface TreatmentPatientInfo {
  isSsnChanged?: boolean;
  originalPatientBirth: string;
  patientRelation?: UserProfileRelationType;
  patientBirth: string;
  patientSsn: string;
  targetId: string;
  patientHeight?: string;
  patientWeight?: string;
  patientNote?: string;
  patientAge?: string;
}

export interface SymptomImageFile {
  thumbnailUrl: string;
  fileUrl: string;
}

export interface TreatmentSymptomInfo {
  symptom: string;
  attachFiles: Array<SymptomImageFile>;
}

export enum TelemedicineGroupCode {
  BACKWOODS_RESIDENT = 'BACKWOODS_RESIDENT',
  MEDICAL_VULNERABLE_AREA_RESIDENT = 'MEDICAL_VULNERABLE_AREA_RESIDENT',
  LONG_TERM_CARE_CLASSIFIER = 'LONG_TERM_CARE_CLASSIFIER',
  REGISTERED_DISABLED = 'REGISTERED_DISABLED',
  INFECTIOUS_DISEASE_CASE = 'INFECTIOUS_DISEASE_CASE',
  UNDER_18_CHILD = 'UNDER_18_CHILD',
  VISITED_PATIENT = 'VISITED_PATIENT',
  OTHERS = 'OTHERS',
}

export enum DoctorDiagnosisFormType {
  General = 'GENERAL',
  InHospitalDelivery = 'IN_HOSPITAL_DELIVERY',
}

export enum HospitalDeliveryCategoryType {
  PICKUP = 'PICKUP',
  POST = 'POST',
  QUICK = 'QUICK',
}

export interface HospitalDeliveryCategory {
  title: string;
  inHospitalDeliveryCategoryType: HospitalDeliveryCategoryType;
}

export interface TreatmentInHospitalDeliveryInfo {
  addressId: string;
  inHospitalDeliveryCategoryType?: HospitalDeliveryCategoryType;
}

export interface TreatmentDispensationInfo {
  dispensationUnit: string;
  treatmentMedicineStrengthId?: string;
}

export enum SsnInputStatus {
  Unfinished = 'UNFINISHED',
  Completed = 'COMPLETE',
  Errored = 'ERRORED',
}

export interface GeoPoint {
  latitude: number;
  longitude: number;
}

export type TreatmentRequestForm =
  | {
      hasVideoRequestCallType: boolean;
      patient: TreatmentPatientInfo;
      symptomInfo: TreatmentSymptomInfo;
      paymentId: string;
      requestForVideoCall?: DiagnosisRequestCallType;
      hasMultiplePatients: boolean; // 가족과 함께 진료 보기 여부
      healthInformationProvided: boolean; // 내 건강정보 같이 보내기 체크 여부
      isAgreement: boolean;
      treatmentMedicineId?: string | null;
      installmentMonth?: number;
      doctorId: string;
      doctorPid?: string;
      bookingTargetDate: string; // ex. 2024-01-11 / 2023-11-23
      startTimeSlotValue: number; // 예약 슬롯 값
      endTimeSlotValue: number;
      isAsapRequest: boolean;
      requestUsePointForNonBenefitDiagnosis?: boolean; // 비급여 진료 포인트 사용 여부
      diagnosisFormType: DoctorDiagnosisFormType.General;
      treatmentMedicineUnitId?: string;
      treatmentMedicineStrengthId?: string;
      isVisitBrand?: boolean;
      medicineType?: DoctorMedicineType;
      categoryType?: DiagnosisCategoryType;
      categoryId?: string;
      inflowType: TreatmentRequestInflowType | null;
      contactType: EDiagnosisContactType;
      pharmacyId?: string;
      dispensationInfo?: TreatmentDispensationInfo;
      priceType?: EDiagnosisPriceType;
      sortKey?: EDoctorSortType;
      specialistOnly?: boolean;
      vaccinationTypeId?: string;
      vaccinationUnitId?: string;
      vaccinationFreePolicyId?: string;
      geoPoint?: GeoPoint;
    }
  | {
      hasVideoRequestCallType: boolean;
      patient: TreatmentPatientInfo;
      symptomInfo: TreatmentSymptomInfo;
      paymentId: string;
      requestForVideoCall?: DiagnosisRequestCallType;
      hasMultiplePatients: boolean; // 가족과 함께 진료 보기 여부
      healthInformationProvided: boolean; // 내 건강정보 같이 보내기 체크 여부
      isAgreement: boolean;
      treatmentMedicineId?: string | null;
      treatmentMedicineUnitId?: string;
      treatmentMedicineStrengthId?: string;
      installmentMonth?: number;
      doctorId: string;
      doctorPid?: string;
      bookingTargetDate: string; // ex. 2024-01-11 / 2023-11-23
      startTimeSlotValue: number; // 예약 슬롯 값
      endTimeSlotValue: number;
      isAsapRequest: boolean;
      requestUsePointForNonBenefitDiagnosis?: boolean; // 비급여 진료 포인트 사용 여부
      diagnosisFormType: DoctorDiagnosisFormType.InHospitalDelivery;
      delivery: TreatmentInHospitalDeliveryInfo;
      isVisitBrand?: boolean;
      medicineType: DoctorMedicineType;
      categoryType?: DiagnosisCategoryType;
      categoryId?: string;
      inflowType: TreatmentRequestInflowType | null;
      contactType: EDiagnosisContactType;
      pharmacyId?: string;
      dispensationInfo?: TreatmentDispensationInfo;
      priceType?: EDiagnosisPriceType;
      sortKey?: EDoctorSortType;
      specialistOnly?: boolean;
      vaccinationTypeId?: string;
      vaccinationUnitId?: string;
      vaccinationFreePolicyId?: string;
      geoPoint?: GeoPoint;
    };

const DELIVERY_CATEGORY_TITLE_MAP: Record<HospitalDeliveryCategoryType, string> = {
  [HospitalDeliveryCategoryType.PICKUP]: '방문해서 받을게요',
  [HospitalDeliveryCategoryType.POST]: '택배로 받을게요',
  [HospitalDeliveryCategoryType.QUICK]: '퀵으로 받을게요',
};

const HOSPITAL_DELIVERY_CATEGORIES: Array<HospitalDeliveryCategory> = Object.values(HospitalDeliveryCategoryType).map(
  (hospitalDeliveryCategoryType) => {
    return {
      title: DELIVERY_CATEGORY_TITLE_MAP[`${hospitalDeliveryCategoryType}`],
      inHospitalDeliveryCategoryType: hospitalDeliveryCategoryType,
    };
  }
);

const DIET_CATEGORY_ID = '12';

export class TreatmentRequest {
  private ETC_TREATMENT_MEDICINE_ID = null;

  constructor(public form: TreatmentRequestForm) {}

  isInHospitalDelivery(): boolean {
    return this.form.diagnosisFormType === DoctorDiagnosisFormType.InHospitalDelivery;
  }

  isQuickMatchRequest(): boolean {
    return this.form.inflowType === TreatmentRequestInflowType.QUICK;
  }

  isWebQuickScheduleRequest(): boolean {
    return this.form.inflowType === TreatmentRequestInflowType.WEB_QUICK;
  }

  hasScheduleModeSelected(): boolean {
    return (
      this.form.inflowType === TreatmentRequestInflowType.WEB_QUICK ||
      this.form.inflowType === TreatmentRequestInflowType.WEB_GENERAL
    );
  }

  shouldShowAsapRequest(): boolean {
    return this.isNonF2FTreatmentRequest() && !this.isQuickMatchRequest();
  }

  isOrientalTreatmentRequest(): boolean {
    return this.form.medicineType === DoctorMedicineType.OrientalDoctor;
  }

  isF2FTreatmentRequest(): boolean {
    return this.form.contactType === EDiagnosisContactType.F2F;
  }

  isDietF2FTreatmentRequest(): boolean {
    return (
      this.form.categoryType === DiagnosisCategoryType.Treatment &&
      this.form.categoryId === DIET_CATEGORY_ID &&
      this.isF2FTreatmentRequest()
    );
  }

  isDietNonF2FTreatmentRequest(): boolean {
    return (
      this.form.categoryType === DiagnosisCategoryType.Treatment &&
      this.form.categoryId === DIET_CATEGORY_ID &&
      this.isNonF2FTreatmentRequest()
    );
  }

  isNonF2FTreatmentRequest(): boolean {
    return this.form.contactType === EDiagnosisContactType.NonF2F;
  }

  isOrientalNonF2FTreatmentRequest(): boolean {
    return this.isOrientalTreatmentRequest() && this.isNonF2FTreatmentRequest();
  }

  isCategorySelected(): boolean {
    return !!this.form.categoryId && !!this.form.categoryType;
  }

  hasVideoCallRequest(): boolean {
    return this.form.hasVideoRequestCallType;
  }

  getCategory(): {
    categoryId: string;
    categoryType: DiagnosisCategoryType;
  } | null {
    const isCategorySelected = this.isCategorySelected();

    if (!isCategorySelected) {
      return null;
    }

    return {
      categoryId: this.form.categoryId!,
      categoryType: this.form.categoryType!,
    };
  }

  get videoCallRequestTitle() {
    if (!this.hasVideoCallRequest()) {
      return '';
    }

    if (this.form.requestForVideoCall === undefined) {
      return '진료 방식';
    }
    return this.form.requestForVideoCall === DiagnosisRequestCallType.Video ? '영상 진료' : '음성 진료';
  }

  updateSymptom(symptom: string): TreatmentRequestForm {
    this.form = {
      ...this.form,
      symptomInfo: {
        ...this.form.symptomInfo,
        symptom,
      },
    };

    return this.form;
  }

  updatePatientSsn(patientSsn: string): TreatmentRequestForm {
    this.form = {
      ...this.form,
      patient: {
        ...this.form.patient,
        patientSsn,
      },
    };

    return this.form;
  }

  resetPatientSsn(): TreatmentRequestForm {
    this.form = {
      ...this.form,
      patient: {
        ...this.form.patient,
        patientSsn: '',
      },
    };

    return this.form;
  }

  selectCard(paymentId: string): TreatmentRequestForm {
    this.form = {
      ...this.form,
      paymentId,
    };
    return this.form;
  }

  updateInstallment(installmentMonth: number): TreatmentRequestForm {
    this.form = {
      ...this.form,
      installmentMonth,
    };
    return this.form;
  }

  initializeTreatmentRequestFromDoctor(doctor: TreatmentRequestDoctor) {
    this.form = {
      ...this.form,
      doctorId: doctor.doctorId,
      medicineType: doctor.medicineType,
      hasVideoRequestCallType: this.isNonF2FTreatmentRequest() ? doctor.readyForVideoCall : false,
      requestForVideoCall:
        this.isNonF2FTreatmentRequest() && doctor.readyForVideoCall ? undefined : DiagnosisRequestCallType.Audio,
      ...(doctor.diagnosisFormType === DoctorDiagnosisFormType.InHospitalDelivery
        ? {
            diagnosisFormType: DoctorDiagnosisFormType.InHospitalDelivery,
            delivery: {
              addressId: '',
              inHospitalDeliveryCategoryType: undefined,
            },
            isVisitBrand: undefined,
          }
        : {
            diagnosisFormType: DoctorDiagnosisFormType.General,
          }),
    };
  }

  updateTreatmentMedicineId(treatmentMedicineId: string | null) {
    this.form = {
      ...this.form,
      treatmentMedicineId,
    };
  }

  selectBookingTargetDate(bookingTargetDate: string) {
    this.form = {
      ...this.form,
      bookingTargetDate,
      startTimeSlotValue: -1,
      endTimeSlotValue: -1,
    };
  }

  selectBookingTimeSlot(startTimeSlotValue: number, endTimeSlotValue: number): TreatmentRequestForm {
    this.form = {
      ...this.form,
      startTimeSlotValue,
      endTimeSlotValue,
    };

    return this.form;
  }

  updateDoctorPid(doctorPublicId: string): TreatmentRequestForm {
    this.form = {
      ...this.form,
      doctorPid: doctorPublicId,
    };
    return this.form;
  }

  toggleAsapRequest(): TreatmentRequestForm {
    this.form = {
      ...this.form,
      isAsapRequest: !this.form.isAsapRequest,
    };

    return this.form;
  }

  setScheduleMode(mode: TreatmentRequestInflowType): TreatmentRequestForm {
    const shouldClearSchedule = mode === TreatmentRequestInflowType.WEB_QUICK;

    this.form = {
      ...this.form,
      inflowType: mode,
      bookingTargetDate: shouldClearSchedule ? '' : this.form.bookingTargetDate,
      startTimeSlotValue: shouldClearSchedule ? -1 : this.form.startTimeSlotValue,
      endTimeSlotValue: shouldClearSchedule ? -1 : this.form.endTimeSlotValue,
    };

    return this.form;
  }

  toggleQuickScheduleMode(): TreatmentRequestForm {
    const isCurrentlyQuickMode = this.isWebQuickScheduleRequest();
    const newMode = isCurrentlyQuickMode
      ? TreatmentRequestInflowType.WEB_GENERAL
      : TreatmentRequestInflowType.WEB_QUICK;
    return this.setScheduleMode(newMode);
  }

  hasEtcTreatmentMedicine(): boolean {
    return this.form.treatmentMedicineId === this.ETC_TREATMENT_MEDICINE_ID;
  }

  updateVisitBrand(isVisitBrand: boolean): TreatmentRequestForm {
    this.form = {
      ...this.form,
      isVisitBrand,
    };

    return this.form;
  }

  updateRequestCallType(requestForVideoCall: DiagnosisRequestCallType): TreatmentRequestForm {
    this.form = {
      ...this.form,
      requestForVideoCall,
    };

    return this.form;
  }

  updateUserProfile(patient: Partial<TreatmentPatientInfo>) {
    this.form = {
      ...this.form,
      patient: {
        ...this.form.patient,
        ...patient,
      },
    };
  }

  updateSymptomImages(attachFiles: Array<SymptomImageFile>): TreatmentRequestForm {
    this.form = {
      ...this.form,
      symptomInfo: {
        ...this.form.symptomInfo,
        attachFiles: [...this.form.symptomInfo.attachFiles, ...attachFiles],
      },
    };

    return this.form;
  }

  removeSymptomImage(imageIndex: number): TreatmentRequestForm {
    this.form = {
      ...this.form,
      symptomInfo: {
        ...this.form.symptomInfo,
        attachFiles: this.form.symptomInfo.attachFiles.filter((_, index) => imageIndex !== index),
      },
    };

    return this.form;
  }

  get deliveryCategoryList(): Array<HospitalDeliveryCategory> {
    /* 퀵 배송 수단을 숨겨달라는 요청에 의해 임시 필터링 */
    return HOSPITAL_DELIVERY_CATEGORIES.filter(
      (category) => category.inHospitalDeliveryCategoryType !== HospitalDeliveryCategoryType.QUICK
    );
  }

  updateInHospitalDeliveryType(
    form: TreatmentRequestForm,
    inHospitalDeliveryCategoryType: HospitalDeliveryCategoryType
  ): TreatmentRequestForm {
    if (form.diagnosisFormType === DoctorDiagnosisFormType.General) {
      return form;
    }

    return {
      ...form,
      delivery: {
        ...form.delivery,
        inHospitalDeliveryCategoryType,
      },
    };
  }

  selectAddress(form: TreatmentRequestForm, addressId: string): TreatmentRequestForm {
    if (form.diagnosisFormType === DoctorDiagnosisFormType.General) {
      return form;
    }

    return {
      ...form,
      delivery: {
        ...form.delivery,
        addressId,
      },
    };
  }

  updatePharmacyId(pharmacyId: string) {
    this.form = {
      ...this.form,
      pharmacyId,
    };
  }

  updateDispensationInfo(dispensationInfo: TreatmentDispensationInfo) {
    this.form = {
      ...this.form,
      dispensationInfo,
    };
  }

  getForm(): TreatmentRequestForm {
    return { ...this.form };
  }

  shouldShowPointUseRequest(): boolean {
    if (!this.form.treatmentMedicineId) {
      return false;
    }

    return !this.hasEtcTreatmentMedicine();
  }

  get deliveryTypeText(): string {
    if (this.form.diagnosisFormType === DoctorDiagnosisFormType.General) {
      return '';
    }

    if (!this.form.delivery.inHospitalDeliveryCategoryType) {
      return '약은 어떻게 받으시겠어요?';
    }

    return DELIVERY_CATEGORY_TITLE_MAP[this.form.delivery.inHospitalDeliveryCategoryType];
  }

  get deliveryTypeSelectTitle(): string {
    if (this.form.diagnosisFormType === DoctorDiagnosisFormType.General) {
      return '';
    }

    switch (this.form.delivery?.inHospitalDeliveryCategoryType) {
      case HospitalDeliveryCategoryType.PICKUP:
        return '어디서 진료를 받고 있으신가요?';
      case HospitalDeliveryCategoryType.POST:
      case HospitalDeliveryCategoryType.QUICK:
        return '배송지를 입력해주세요.';
      default:
        return '';
    }
  }

  getCurrentSsnStatus(): SsnInputStatus {
    if (
      !this.form.patient.patientBirth ||
      this.form.patient.patientBirth.length !== 6 ||
      this.form.patient.patientSsn.length !== 7
    ) {
      return SsnInputStatus.Unfinished;
    }

    if (
      validateSsnCheck(this.form.patient.patientBirth, this.form.patient.patientSsn, this.form.patient.isSsnChanged)
    ) {
      return SsnInputStatus.Completed;
    }

    return SsnInputStatus.Errored;
  }

  validateSsn(): boolean {
    return this.getCurrentSsnStatus() === SsnInputStatus.Completed;
  }

  isSelectedTreatmentMedicine() {
    return this.form.treatmentMedicineId !== undefined;
  }

  getTreatmentMedicineName(categoryInfo?: CategoryInfo): string {
    if (!categoryInfo || !categoryInfo.hasMedicine()) {
      return '';
    }

    const foundMedicineName = categoryInfo.medicines?.find(
      (medicine) => medicine.id === this.form.treatmentMedicineId
    )?.medicineName;

    return foundMedicineName ? ` / ${foundMedicineName}` : '';
  }

  toggleRequestUsePoint(): TreatmentRequestForm {
    this.form = {
      ...this.form,
      requestUsePointForNonBenefitDiagnosis: !this.form.requestUsePointForNonBenefitDiagnosis,
    };
    return this.form;
  }

  updateRequestAgreement(isAgreement: boolean): TreatmentRequestForm {
    this.form = {
      ...this.form,
      isAgreement,
    };

    return this.form;
  }

  getSortKey(): EDoctorSortType {
    return this.form.sortKey ?? EDoctorSortType.DEFAULT;
  }

  getSortKeyName(): string {
    switch (this.getSortKey()) {
      case EDoctorSortType.DEFAULT:
        return '기본순';
      case EDoctorSortType.REVIEW_COUNT:
        return '후기많은순';
      case EDoctorSortType.REVIEW_AVERAGE_RATING:
        return '별점높은순';
      case EDoctorSortType.DISTANCE:
        return '가까운순';
      default:
        return '기본순';
    }
  }

  updateSortKey(sortKey: EDoctorSortType): TreatmentRequestForm {
    this.form = {
      ...this.form,
      sortKey,
    };
    return this.form;
  }

  updateSpecialistOnly(specialistOnly: boolean): TreatmentRequestForm {
    this.form = {
      ...this.form,
      specialistOnly,
    };
    return this.form;
  }

  getIsSpecialistOnly(): boolean {
    return this.form.specialistOnly ?? false;
  }

  updateGeoPoint(geoPoint: GeoPoint): TreatmentRequestForm {
    this.form = {
      ...this.form,
      geoPoint,
    };
    return this.form;
  }

  getGeoPoint(): GeoPoint | undefined {
    return this.form.geoPoint;
  }

  selectTreatmentRequestDepartment(department: TreatmentRequestDoctorDepartment) {
    this.form = {
      ...this.form,
      categoryId: department.id,
      categoryType: DiagnosisCategoryType.Department,
      treatmentMedicineId: undefined,
    };
  }

  selectTreatmentRequestTreatment(treatment: TreatmentRequestDoctorTreatment) {
    this.form = {
      ...this.form,
      categoryId: treatment.id,
      categoryType: DiagnosisCategoryType.Treatment,
      treatmentMedicineId: undefined,
    };
  }

  validateDiagnosisStepWithoutDetailSymptom(categoryInfo?: CategoryInfo): boolean {
    if (this.isInHospitalDelivery() && this.form.isVisitBrand === undefined) {
      return false;
    }

    /* 스케쥴 미선택 케이스 */
    if (this.form.startTimeSlotValue < 0 || this.form.endTimeSlotValue < 0) {
      return false;
    }

    if (this.form.requestForVideoCall === undefined) {
      return false;
    }

    /* 주민번호 검증 실패 케이스 */
    if (!this.validateSsn()) {
      return false;
    }

    if (!this.isCategorySelected()) {
      return false;
    }

    /* 비급여에서 세부 카테고리를 가졌을 때 아직 약품을 선택하지 않은 케이스 */
    return !(!!categoryInfo?.hasMedicine() && !this.isSelectedTreatmentMedicine());
  }

  validateDiagnosisStepByWebDiagnosis(): boolean {
    /* 가장 빠른 시간에 진료받기 모드가 아닐 때만 스케쥴 검증 */
    if (!this.isWebQuickScheduleRequest()) {
      if (this.form.startTimeSlotValue < 0 || this.form.endTimeSlotValue < 0) {
        return false;
      }
    }

    /* 주민번호 검증 실패 케이스 */
    if (!this.validateSsn()) {
      return false;
    }

    if (!this.isCategorySelected()) {
      return false;
    }

    if (this.form.symptomInfo.symptom.length === 0) {
      return false;
    }
    return true;
  }

  validateDiagnosisStep(categoryInfo?: CategoryInfo): boolean {
    /* 원내배송일때 이전에 브랜드 이용유무 확인을 하지 않은 케이스 */

    if (!this.validateDiagnosisStepWithoutDetailSymptom(categoryInfo)) {
      return false;
    }
    return !!this.form.symptomInfo.symptom;
  }

  private validatePaymentStepBeforeAgreementCheck(): boolean {
    if (this.form.diagnosisFormType === DoctorDiagnosisFormType.InHospitalDelivery) {
      if (!this.form.delivery) {
        return false;
      }

      if (!this.form.delivery.inHospitalDeliveryCategoryType) {
        return false;
      }

      if (!this.form.delivery.addressId) {
        return false;
      }
    }

    /* 카드가 선택되어 있는지 체크 */
    return !!this.form.paymentId;
  }

  isDisabledApproveCheckButton() {
    if (!this.validatePaymentStepBeforeAgreementCheck()) {
      return false;
    }

    /* 약관을 제외하고 모든 값이 선택되어 있다면 진료 약관 확인 및 동의 버튼 노출 */
    return !this.form.isAgreement;
  }

  validatePaymentStep(): boolean {
    if (!this.validatePaymentStepBeforeAgreementCheck()) {
      return false;
    }

    /* 약관 승인 체크 */
    return this.form.isAgreement;
  }

  updateDoctorId(doctorId: string): TreatmentRequestForm {
    this.form = {
      ...this.form,
      doctorId,
    };
    return this.form;
  }

  getDoctorId(): string {
    return this.form.doctorId;
  }
}

export enum ScheduleStatus {
  BOOKABLE = 'BOOKABLE',
  FULLY_BOOKED = 'FULLY_BOOKED',
  HOLIDAY = 'HOLIDAY',
}

export enum BookingTimeScheduleStatus {
  Bookable = 'BOOKABLE',
  Disabled = 'DISABLED',
  FullyBooked = 'FULLY_BOOKED',
}

export interface DaySchedule {
  displayName: string;
  status: ScheduleStatus;
  isBookableDay: boolean;
  targetDate: string;
  morningSchedules: Array<BookingTimeSchedule>;
  afternoonSchedules: Array<BookingTimeSchedule>;
}

export interface DayScheduleGroupedByTimePeriod {
  displayName: string;
  status: ScheduleStatus;
  isBookableDay: boolean;
  targetDate: string;
  dawnSchedules: Array<BookingTimeSchedule>;
  morningSchedules: Array<BookingTimeSchedule>;
  afternoonSchedules: Array<BookingTimeSchedule>;
  nightSchedules: Array<BookingTimeSchedule>;
}

export interface DiagnosisTimeSlot {
  value: number;
  displayTime: string;
  fromDisplayTime: string;
  toDisplayTime: string;
}

export interface BookingTimeSchedule {
  isBookable: boolean;
  bookingStatus: BookingTimeScheduleStatus;
  startTimeSlot: DiagnosisTimeSlot;
  endTimeSlot: DiagnosisTimeSlot;
  remainingCount: number;
  isSelected: (startTimeSlotValue: number, endTimeSlotValue: number) => boolean;
}

export class NonFaceToFaceBookingSchedulesInfo {
  private AT_NOON_TIME_SLOT_VALUE = 72;

  private DAWN_END_VALUE = 46;
  private MORNING_START_VALUE = 48;
  private MORNING_END_VALUE = 70;
  private AFTERNOON_START_VALUE = 72;
  private AFTERNOON_END_VALUE = 118;
  private NIGHT_START_VALUE = 120;

  constructor(
    private _schedules: Array<{
      displayName: string;
      status: DayScheduleStatus;
      targetDate: string;
      schedules: Array<{
        bookingStatus: BookingScheduleType;
        startTimeSlot: DiagnosisTimeSlot;
        endTimeSlot: DiagnosisTimeSlot;
        remainingCount: number;
      }>;
    }>
  ) {}

  private getScheduleStatus(status: DayScheduleStatus): ScheduleStatus {
    switch (status) {
      case DayScheduleStatus.Bookable:
        return ScheduleStatus.BOOKABLE;
      case DayScheduleStatus.FullyBooked:
        return ScheduleStatus.FULLY_BOOKED;
      case DayScheduleStatus.Holiday:
        return ScheduleStatus.HOLIDAY;
    }
  }

  private getBookingTimeStatus(status: BookingScheduleType): BookingTimeScheduleStatus {
    switch (status) {
      case BookingScheduleType.Bookable:
        return BookingTimeScheduleStatus.Bookable;
      case BookingScheduleType.Disabled:
        return BookingTimeScheduleStatus.Disabled;
      case BookingScheduleType.FullyBooked:
        return BookingTimeScheduleStatus.FullyBooked;
    }
  }

  private convertSchedule(schedule: {
    bookingStatus: BookingScheduleType;
    startTimeSlot: DiagnosisTimeSlot;
    endTimeSlot: DiagnosisTimeSlot;
    remainingCount: number;
  }) {
    return {
      bookingStatus: this.getBookingTimeStatus(schedule.bookingStatus),
      isBookable: this.getBookingTimeStatus(schedule.bookingStatus) === BookingTimeScheduleStatus.Bookable,
      startTimeSlot: {
        value: schedule.startTimeSlot.value,
        displayTime: schedule.startTimeSlot.displayTime,
        fromDisplayTime: schedule.startTimeSlot.fromDisplayTime,
        toDisplayTime: schedule.startTimeSlot.toDisplayTime,
      },
      endTimeSlot: {
        value: schedule.endTimeSlot.value,
        displayTime: schedule.endTimeSlot.displayTime,
        fromDisplayTime: schedule.endTimeSlot.fromDisplayTime,
        toDisplayTime: schedule.endTimeSlot.toDisplayTime,
      },
      remainingCount: schedule.remainingCount,
      isSelected: (startTimeSlotValue: number, endTimeSlotValue: number) => {
        return schedule.startTimeSlot.value === startTimeSlotValue && schedule.endTimeSlot.value === endTimeSlotValue;
      },
    };
  }

  private filterSchedulesByTimeRange(
    schedules: Array<{
      bookingStatus: BookingScheduleType;
      startTimeSlot: DiagnosisTimeSlot;
      endTimeSlot: DiagnosisTimeSlot;
      remainingCount: number;
    }>,
    startValue: number,
    endValue?: number
  ): Array<BookingTimeSchedule> {
    return schedules
      .filter((schedule) => {
        const value = schedule.startTimeSlot.value;
        return endValue !== undefined ? value >= startValue && value <= endValue : value >= startValue;
      })
      .map((schedule) => this.convertSchedule(schedule));
  }

  get daySchedules(): Array<DaySchedule> {
    return this._schedules.map((daySchedule) => {
      return {
        displayName: daySchedule.displayName,
        status: this.getScheduleStatus(daySchedule.status),
        isBookableDay: this.getScheduleStatus(daySchedule.status) === ScheduleStatus.BOOKABLE,
        targetDate: daySchedule.targetDate,
        morningSchedules: daySchedule.schedules
          .filter((schedule) => schedule.startTimeSlot.value < this.AT_NOON_TIME_SLOT_VALUE)
          .map((schedule) => this.convertSchedule(schedule)),
        afternoonSchedules: daySchedule.schedules
          .filter((schedule) => schedule.startTimeSlot.value >= this.AT_NOON_TIME_SLOT_VALUE)
          .map((schedule) => this.convertSchedule(schedule)),
      };
    });
  }

  get daySchedulesGroupedByTimePeriod(): Array<DayScheduleGroupedByTimePeriod> {
    return this._schedules.map((daySchedule) => {
      return {
        displayName: daySchedule.displayName,
        status: this.getScheduleStatus(daySchedule.status),
        isBookableDay: this.getScheduleStatus(daySchedule.status) === ScheduleStatus.BOOKABLE,
        targetDate: daySchedule.targetDate,
        dawnSchedules: this.filterSchedulesByTimeRange(daySchedule.schedules, 0, this.DAWN_END_VALUE),
        morningSchedules: this.filterSchedulesByTimeRange(
          daySchedule.schedules,
          this.MORNING_START_VALUE,
          this.MORNING_END_VALUE
        ),
        afternoonSchedules: this.filterSchedulesByTimeRange(
          daySchedule.schedules,
          this.AFTERNOON_START_VALUE,
          this.AFTERNOON_END_VALUE
        ),
        nightSchedules: this.filterSchedulesByTimeRange(daySchedule.schedules, this.NIGHT_START_VALUE),
      };
    });
  }

  findSelectedDaySchedule(treatmentRequest: TreatmentRequest | DiagnosisBookingChange): DaySchedule | null {
    const { bookingTargetDate } = treatmentRequest.form;
    return this.daySchedules.find((schedule) => schedule.targetDate === bookingTargetDate) || null;
  }

  findSelectedDayScheduleGroupedByTimePeriod(
    treatmentRequest: TreatmentRequest | DiagnosisBookingChange
  ): DayScheduleGroupedByTimePeriod | null {
    const { bookingTargetDate } = treatmentRequest.form;
    return this.daySchedulesGroupedByTimePeriod.find((schedule) => schedule.targetDate === bookingTargetDate) || null;
  }

  private findSelectedTimeSlot(
    treatmentRequest: TreatmentRequest | DiagnosisBookingChange
  ): BookingTimeSchedule | null {
    const { startTimeSlotValue, endTimeSlotValue } = treatmentRequest.form;

    const selectedDay = this.findSelectedDaySchedule(treatmentRequest);

    if (!selectedDay) {
      return null;
    }

    return (
      selectedDay.morningSchedules.find((time) => time.isSelected(startTimeSlotValue, endTimeSlotValue)) ||
      selectedDay.afternoonSchedules.find((time) => time.isSelected(startTimeSlotValue, endTimeSlotValue)) ||
      null
    );
  }

  getSelectedTimeSlotTitle(treatmentRequest: TreatmentRequest, shouldShowQuickModeTitle = false): string {
    if (treatmentRequest.isWebQuickScheduleRequest() && shouldShowQuickModeTitle) {
      return '가장 빠른 시간에 진료받기';
    }

    const selectedDay = this.findSelectedDaySchedule(treatmentRequest);
    const selectedTimeSlot = this.findSelectedTimeSlot(treatmentRequest);

    if (!selectedDay || !selectedTimeSlot) {
      return '진료 날짜';
    }

    return `${dayjs(`${selectedDay.targetDate}`).format('M월 D일')} ${
      selectedTimeSlot?.startTimeSlot.fromDisplayTime
    }~${selectedTimeSlot?.endTimeSlot.toDisplayTime}`;
  }

  findNearestBookingDay() {
    return this.daySchedules.find((daySchedule) => daySchedule.isBookableDay) || null;
  }

  findNearestBookingDayAndTimeSlot() {
    const nearestDaySchedule = this.findNearestBookingDay();

    if (!nearestDaySchedule) {
      return {
        targetDate: '',
        startTimeSlotValue: -1,
        endTimeSlotValue: -1,
      };
    }

    const bookableMorningTimeSlot = nearestDaySchedule.morningSchedules.find((timeSlot) => timeSlot.isBookable);
    const bookableAfternoonTimeSlot = nearestDaySchedule.afternoonSchedules.find((timeSlot) => timeSlot.isBookable);

    return {
      targetDate: nearestDaySchedule.targetDate,
      startTimeSlotValue: bookableMorningTimeSlot
        ? bookableMorningTimeSlot.startTimeSlot.value
        : bookableAfternoonTimeSlot
          ? bookableAfternoonTimeSlot.startTimeSlot.value
          : -1,
      endTimeSlotValue: bookableMorningTimeSlot
        ? bookableMorningTimeSlot.endTimeSlot.value
        : bookableAfternoonTimeSlot
          ? bookableAfternoonTimeSlot.endTimeSlot.value
          : -1,
    };
  }

  getQuickestAvailableMinutes(): number | null {
    const nearestDaySchedule = this.findNearestBookingDay();

    if (!nearestDaySchedule) {
      return null;
    }

    const bookableMorningTimeSlot = nearestDaySchedule.morningSchedules.find((timeSlot) => timeSlot.isBookable);
    const bookableAfternoonTimeSlot = nearestDaySchedule.afternoonSchedules.find((timeSlot) => timeSlot.isBookable);

    const firstBookableSlot = bookableMorningTimeSlot || bookableAfternoonTimeSlot;

    if (!firstBookableSlot) {
      return null;
    }

    const now = dayjs();
    const targetDateTime = dayjs(nearestDaySchedule.targetDate);
    const endTime = firstBookableSlot.endTimeSlot.displayTime;
    const [hours, minutes] = endTime.split(':').map(Number);

    const slotEndDateTime = targetDateTime.hour(hours).minute(minutes).second(0);
    const diffInMinutes = slotEndDateTime.diff(now, 'minute');

    return diffInMinutes > 0 ? diffInMinutes : null;
  }
}

export interface TreatmentRequestDoctorDepartment {
  id: string;
  name: string;
  description: string;
  iconUrl: string;
}

export interface TreatmentRequestDoctorTreatmentCategory {
  id: string;
  name: string;
  iconUrl: string;
  treatments: Array<TreatmentRequestDoctorTreatment>;
}

export interface TreatmentRequestDoctorTreatment {
  id: string;
  name: string;
  medicines: Array<{
    id: string | null;
    medicineName: string;
  }>;
}

export class TreatmentRequestDoctor {
  constructor(
    private _doctor: {
      name: string;
      doctorId: string;
      readyForVideoCall: boolean;
      brandName?: string;
      diagnosisFormType?: DiagnosisFormType;
      medicineType: MedicineType;
      departments: Array<{
        id: string;
        name: string;
        description: string;
        iconUrl: string;
      }>;
      profileImageUrl: string;
      treatmentCategories: Array<{
        id: string;
        name: string;
        iconUrl: string;
        treatments: Array<{
          id: string;
          name: string;
          medicines: Array<{
            id: string;
            medicineName: string;
          }>;
        }>;
      }>;
      doctorVaccinations?: Array<{
        treatmentId: string;
        treatmentName: string;
        supportedFreePolicyIds: string[];
        vaccinationTypes: Array<{
          vaccinationTypeId: string;
          name: string;
          products: Array<{
            vaccineProductId: string;
            vaccinationTypeUnitId: string;
            price: number;
            isAvailable: boolean;
          }>;
        }>;
      }>;
    }
  ) {}

  get doctorId(): string {
    return this._doctor.doctorId;
  }

  get doctorName(): string {
    return this._doctor.name;
  }

  get readyForVideoCall(): boolean {
    return this._doctor.readyForVideoCall;
  }

  private getDoctorMedicineType = (medicineType: MedicineType) => {
    switch (medicineType) {
      case MedicineType.Dentist:
        return DoctorMedicineType.Dentist;
      case MedicineType.Doctor:
        return DoctorMedicineType.Doctor;
      case MedicineType.OrientalDoctor:
        return DoctorMedicineType.OrientalDoctor;
      default:
        return DoctorMedicineType.Unknown;
    }
  };

  get medicineType(): DoctorMedicineType {
    return this.getDoctorMedicineType(this._doctor.medicineType);
  }

  private getDiagnosisFormType = (diagnosisFormType?: DiagnosisFormType) => {
    switch (diagnosisFormType) {
      case DiagnosisFormType.General:
        return DoctorDiagnosisFormType.General;
      case DiagnosisFormType.InHospitalDelivery:
        return DoctorDiagnosisFormType.InHospitalDelivery;
      default:
        return DoctorDiagnosisFormType.General;
    }
  };

  get diagnosisFormType(): DoctorDiagnosisFormType {
    return this.getDiagnosisFormType(this._doctor.diagnosisFormType);
  }

  get departments(): Array<TreatmentRequestDoctorDepartment> {
    return this._doctor.departments;
  }

  /* 비급여 진료항목중에서 그외 진료 및 처방 추가 */
  get treatmentCategories(): Array<TreatmentRequestDoctorTreatmentCategory> {
    return this._doctor.treatmentCategories.map((treatmentCategory) => ({
      id: treatmentCategory.id,
      name: treatmentCategory.name,
      iconUrl: treatmentCategory.iconUrl,
      treatments: treatmentCategory.treatments.map((treatment) => ({
        id: treatment.id,
        name: treatment.name,
        medicines:
          treatment.medicines.length > 0
            ? [...treatment.medicines, { id: null, medicineName: '그 외 진료 및 처방' }]
            : [],
      })),
    }));
  }

  get doctorProfileImage(): string {
    return this._doctor.profileImageUrl;
  }

  get brandName(): string {
    return this._doctor.brandName ?? '';
  }

  get doctorVaccinations() {
    return this._doctor.doctorVaccinations ?? [];
  }

  private findTreatmentByCategoryId(categoryId: string) {
    for (const treatmentCategory of this.treatmentCategories) {
      for (const treatment of treatmentCategory.treatments) {
        if (treatment.id === categoryId) {
          return treatment;
        }
      }
    }
    return null;
  }

  getSelectedCategory(
    params: {
      categoryType: DiagnosisCategoryType;
      categoryId: string;
    } | null
  ): CategoryInfo | undefined {
    if (!params) {
      return undefined;
    }

    const { categoryType, categoryId } = params;

    if (categoryType === DiagnosisCategoryType.Department) {
      const foundDepartment = this.departments.find((department) => department.id === categoryId);

      return foundDepartment
        ? {
            id: foundDepartment.id,
            type: DiagnosisCategoryType.Department,
            name: foundDepartment.name,
            hasMedicine: () => false,
            medicines: [],
          }
        : undefined;
    } else if (categoryType === DiagnosisCategoryType.Treatment) {
      const foundTreatment = this.findTreatmentByCategoryId(categoryId);
      return foundTreatment
        ? {
            id: foundTreatment.id,
            type: DiagnosisCategoryType.Treatment,
            name: foundTreatment.name,
            hasMedicine: () => !!foundTreatment.medicines && foundTreatment.medicines.length > 0,
            medicines: foundTreatment.medicines,
          }
        : undefined;
    } else if (categoryType === DiagnosisCategoryType.Vaccination) {
      const foundVaccination = this.doctorVaccinations.find((vaccination) => vaccination.treatmentId === categoryId);
      return foundVaccination
        ? {
            id: foundVaccination.treatmentId,
            type: DiagnosisCategoryType.Vaccination,
            name: foundVaccination.treatmentName,
            hasMedicine: () => false,
            medicines: [],
          }
        : undefined;
    }

    return undefined;
  }

  getSelectedCategoryName(
    params: {
      categoryType: DiagnosisCategoryType;
      categoryId: string;
    } | null
  ) {
    return this.getSelectedCategory(params)?.name ?? '증상 또는 과목을 선택해주세요';
  }

  hasDepartments(): boolean {
    return this.departments.length > 0;
  }

  hasTreatmentCategories(): boolean {
    return this.treatmentCategories.length > 0;
  }
}
